import React, { useEffect, useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import moment from 'moment';
import { FormikInput, FormikTimePicker, FormikSelect } from '../../../components';
import { debounceIndexCostCentersRequest } from '../../../requests/costCenters';
import { debounceIndexJobManagementsRequest } from '../../../requests/jobManagements';
import { payrollEmployeesRequest } from '../../../requests/payrolls';
import FormikSelectClear from '../../../components/Utils/Select/FormikSelectClear';
import { debounceIndexBranchOfficesRequest } from '../../../requests/branchOffices';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';

const optionsFormat = [
  /* { label: 'Formato Excel', value: 0 },
  { label: 'Formato Texto', value: 1 }, */
  { label: 'Formato CSV', value: 2 }
];

const ALL_SELECTED_LABEL = 'Todos';

const Filters = props => {
  const { currentCompany } = useSelector(state => state.auth);
  const { errors, touched, setFieldValue, values, setFieldTouched, onHide, onRequest } = props;
  const [costCenters, setCostCenters] = useState([]);
  const [areas, setAreas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branchOffices, setBranchOffices] = useState([]);
  const dispatch = useDispatch();

  const resultFetchData = (response, type) => {
    const result = response.data.data;
    return result.map(element => ({
      ...element,
      label: `${type ? `${element.code} - ` : ''} ${element.name}`,
      value: element.id
    }));
  };

  const successEmployees = data => {
    const parsedEmployees = camelCaseEmptyStringRecursive(data);
    return parsedEmployees.map(employee => ({
      label: employee.startDateContract ? `${employee.fullName} - (${employee.startDateContract})` : employee.fullName,
      value: employee.payrollId
    }));
  };

  const employeeDateRange = () => {
    if (values.dateFrom && values.dateTo) {
      const dateFrom = moment(values.dateFrom);
      const dateTo = moment(values.dateTo);
      return { within_date_range: `${dateFrom},${dateTo}` };
    }
    if (values.monthDate && values.yearDate) {
      const month = moment(values.monthDate).format('YYYY/MM/DD');
      const year = moment(values.yearDate).format('YYYY/MM/DD');
      return {
        month_date: month,
        year_date: year
      };
    }
    return false;
  };
  const fetchEmployees = () => {
    if (!employeeDateRange()) return;
    payrollEmployeesRequest({
      dispatch,
      params: {
        paginate: false,
        advance: false,
        filter_by_company: currentCompany.id,
        ...employeeDateRange()
      },
      successCallback: ({ data }) => {
        const employeesData = successEmployees(data.data);
        const allEmployees = [{ label: 'Todos', value: 'all' }, ...employeesData];
        setEmployees(allEmployees);
      }
    });
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.label === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.label === ALL_SELECTED_LABEL;
  };

  const handleNewMulti = (data, field, allowEmpty = false) => {
    const newData = data.map(element => element.value);
    if (allowEmpty && !newData.length) newData.push('');
    setFieldValue(field, newData.includes('all') ? [] : newData);
  };

  const fetchCostCenters = () => {
    debounceIndexCostCentersRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 50
      },
      successCallback: data => {
        setCostCenters(resultFetchData(data));
      }
    });
  };

  const fetchJobManagments = () => {
    debounceIndexJobManagementsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        setAreas(resultFetchData(data));
      }
    });
  };

  const fetchBranchOffices = () => {
    debounceIndexBranchOfficesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        display_length: 40
      },
      successCallback: data => {
        setBranchOffices(resultFetchData(data));
      }
    });
  };

  useEffect(fetchCostCenters, []);
  useEffect(fetchJobManagments, []);
  useEffect(fetchBranchOffices, []);
  useEffect(fetchEmployees, [values.monthDate, values.yearDate]);

  return (
    <Form>
      <Card style={{ zIndex: 'unset' }}>
        <Row>
          <Col className="mt-2">
            <Card className="card-dt">
              <Col className="top-header-green-dt card-header-title-dt pt-1 pb-1">
                Libro de Remuneraciones Electrónico - DT
              </Col>
              <Card.Body className="div-content card-body-padding">
                <Row>
                  <Col sm={12} md={6} xs={12}>
                    <Field name="monthDate">
                      {({ field }) => (
                        <FormikTimePicker
                          abbr
                          label="Mes y Año de proceso"
                          placeholder="Seleccionar mes y año de proceso"
                          dateFormat="MMMM - yyyy"
                          showMonthYearPicker
                          setLocale
                          onChange={date => {
                            setFieldValue(field.name, date);
                            setFieldValue('yearDate', date);
                          }}
                          selected={field.value}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={6} xs={12}>
                    <Field name="format">
                      {({ field }) => (
                        <FormikSelect
                          abbr
                          {...field}
                          label="Formato de Emisión"
                          placeholder="Seleccionar formato de emisión"
                          options={optionsFormat}
                          isClearable
                          onChange={data => setFieldValue(field.name, data ? data.value : '')}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} md={6} xs={12}>
                    <Field name="employees">
                      {({ field }) => (
                        <FormikSelectClear
                          {...field}
                          label="Trabajadores"
                          placeholder="Seleccionar Trabajador"
                          isMulti
                          options={employees}
                          values={values.employee_ids}
                          onChange={data => {
                            handleNewMulti(data || [], 'filterIds', true);
                            setFieldValue(field.name, data || []);
                          }}
                          setFieldTouched={() => setFieldTouched(field.name)}
                          isOptionDisabled={handleDisabledSelection}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={6} xs={12}>
                    <Field name="filterNationalId">
                      {({ field }) => (
                        <FormikInput
                          {...field}
                          label="RUT:"
                          placeholder="Sin puntos (ej: 11111111-1)"
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="filterCostCenter">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Centro de Costos"
                          menuPlacement="top"
                          placeholder="Seleccionar Centro de Costos"
                          options={costCenters}
                          isClearable
                          onChange={data => setFieldValue(field.name, data ? data.value : '')}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="filterJobManagement">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Área"
                          menuPlacement="top"
                          placeholder="Seleccionar Área"
                          options={areas}
                          isClearable
                          onChange={data => setFieldValue(field.name, data ? data.value : '')}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="branchOfficeIds">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          menuPlacement="top"
                          label="Lugar de Prestación de Servicios"
                          placeholder="Seleccionar Lugar de Prestación de Servicios"
                          options={branchOffices}
                          isClearable
                          onChange={data => setFieldValue(field.name, data ? data.value : '')}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card>
      <Row className="d-flex justify-content-end mt-4 mb-4">
        <Col md={2}>
          <Button className="btn-block" type="submit" onClick={onHide} disabled={onRequest}>
            Filtrar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

// const validationSchema = Yup.object().shape({});
const validationSchema = Yup.object().shape({
  monthDate: Yup.string().required('Debes escoger un mes de proceso'),
  yearDate: Yup.mixed().test('is-year', 'Debes escoger un año de proceso', value => {
    return value !== undefined && value !== '' && value !== null;
  }),
  format: Yup.string().required('Debes escoger el formato')
});

const setInitialValues = () => {
  return {
    employees: [],
    filterIds: [],
    filterNationalId: '',
    monthDate: '',
    yearDate: '',
    format: '',
    filterCostCenter: '',
    filterJobManagement: '',
    filterSubsidiary: ''
  };
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(Filters);
